import { useRef } from 'react'
import { Search, X } from 'lucide-react'
import { SearchField } from '../controls/SearchField'
import s from './GridSearch.module.css'

export interface GridSearchProps {
  value: string
  onChange: (value: string) => void
  /** The collection's plural, lowercase or not: "Products" → "Search products". */
  collection: string
  /**
   * Why this grid cannot search, when its data door has no search (a subject
   * door's closed vocabulary, #2444). The field is then present, disabled and
   * says why — never absent, because every interactive grid has one (#2375),
   * and never silently filtering a fetched page instead.
   */
  unavailable?: string
  testId?: string
}

/**
 * THE COLLECTION SEARCH (#2375) — on every interactive grid, at every depth,
 * in the bar's trailing group. Scoped to THIS grid's rows.
 *
 * Kit `SearchField` (a value slot with a leading glyph), a clear ✕ while it
 * holds text, and Escape that clears first and blurs on the second press — so
 * one key both undoes a search and hands focus back without losing a draft.
 * Where the query runs (server or client) is the host's; this owns the field.
 */
export function GridSearch({ value, onChange, collection, unavailable, testId = 'grid-search' }: GridSearchProps) {
  const ref = useRef<HTMLInputElement>(null)
  const noun = collection.toLocaleLowerCase()
  return (
    <span className={s.search} data-grid-search="">
      <SearchField ref={ref} type="search" value={value} data-testid={testId}
        icon={<Search size={14} />}
        aria-label={`Search ${noun}`}
        placeholder={`Search ${noun}`}
        disabled={unavailable != null}
        title={unavailable}
        className={s.input}
        onChange={(event) => onChange(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          event.stopPropagation()
          if (value) onChange('')
          else ref.current?.blur()
        }} />
      {value && unavailable == null && (
        <button type="button" className={s.clear} aria-label={`Clear search for ${noun}`}
          data-testid={`${testId}-clear`}
          onClick={() => { onChange(''); ref.current?.focus() }}>
          <X size={12} strokeWidth={2.5} aria-hidden="true" />
        </button>
      )}
    </span>
  )
}
